import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/10 bg-[#0D0D0D] px-4 py-3.5 text-sm text-white placeholder:text-[#4d4d4d] outline-none transition-colors duration-300 focus:border-[#D4AF37]";

const stressOptions = [
  { value: "1", label: "1 — Minimo" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5 — Moderato" },
  { value: "6", label: "6" },
  { value: "7", label: "7" },
  { value: "8", label: "8" },
  { value: "9", label: "9" },
  { value: "10", label: "10 — Attrito Biologico Critico" },
];

export default function Mentorship() {
  const [spots, setSpots] = useState(null);
  const [form, setForm] = useState({
    nome_completo: "",
    email_aziendale: "",
    livello_stress: "",
    blocco_fisiologico: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/spots`)
      .then((res) => setSpots(res.data))
      .catch(() => setSpots(null));
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.livello_stress) {
      toast.error("Seleziona il tuo livello di stress giornaliero.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/candidature`, {
        nome_completo: form.nome_completo,
        email_aziendale: form.email_aziendale,
        livello_stress: Number(form.livello_stress),
        blocco_fisiologico: form.blocco_fisiologico,
      });
      toast.success("Candidatura inviata con successo.", {
        description:
          "Il team di Joe Fenix valuterà il tuo profilo e ti ricontatterà al più presto.",
      });
      setForm({
        nome_completo: "",
        email_aziendale: "",
        livello_stress: "",
        blocco_fisiologico: "",
      });
    } catch (err) {
      toast.error("Invio non riuscito.", {
        description: "Verifica i dati inseriti e riprova tra qualche istante.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="candidatura"
      data-testid="mentorship-section"
      className="relative border-t border-[#D4AF37]/10 px-6 py-28 md:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-[#B87333]/5 blur-[130px]"
      />
      <div className="relative mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-[#B87333]">
            Mentorship High-Ticket
          </p>
          <h2
            data-testid="mentorship-title"
            className="font-serif-lux text-2xl tracking-tight text-[#D4AF37] sm:text-3xl lg:text-4xl"
          >
            CANDIDATURA FORMALE: SBLOCCA IL TUO MASSIMO VOLTAGGIO.
          </h2>
          <p
            data-testid="mentorship-subtitle"
            className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3]"
          >
            Invia i tuoi dati per richiedere l'accesso alla Mentorship Privata
            High-Ticket con Joe Fenix. I posti sono rigorosamente limitati ed
            esclusivi.
          </p>
        </motion.div>

        {spots !== null && (
          <motion.p
            data-testid="spots-counter"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mx-auto mt-10 flex w-fit items-center gap-3 border border-[#B87333]/40 bg-[#B87333]/10 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#D2853D]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#B87333] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#B87333]" />
            </span>
            Solo {spots.rimanenti} posti rimanenti su {spots.totale}
          </motion.p>
        )}

        <motion.form
          onSubmit={handleSubmit}
          data-testid="candidatura-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 space-y-7 border border-[#D4AF37]/25 bg-[#141414] p-8 shadow-[0_0_80px_rgba(0,0,0,0.5)] md:p-12"
        >
          <div>
            <label
              htmlFor="nome-completo"
              className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
            >
              Nome Completo
            </label>
            <input
              id="nome-completo"
              data-testid="input-nome-completo"
              type="text"
              required
              value={form.nome_completo}
              onChange={update("nome_completo")}
              placeholder="Dr. Mario Rossi"
              className={inputCls}
            />
          </div>
          <div>
            <label
              htmlFor="email-aziendale"
              className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
            >
              Email Aziendale
            </label>
            <input
              id="email-aziendale"
              data-testid="input-email-aziendale"
              type="email"
              required
              value={form.email_aziendale}
              onChange={update("email_aziendale")}
              placeholder="m.rossi@azienda.it"
              className={inputCls}
            />
          </div>
          <div>
            <label
              htmlFor="livello-stress"
              className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
            >
              Livello di Stress Giornaliero (1-10)
            </label>
            <select
              id="livello-stress"
              data-testid="select-livello-stress"
              required
              value={form.livello_stress}
              onChange={update("livello_stress")}
              className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22%3E%3Cpath d=%22M1 1l5 5 5-5%22 stroke=%22%23D4AF37%22 stroke-width=%221.5%22 fill=%22none%22/%3E%3C/svg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10 ${form.livello_stress === "" ? "text-[#4d4d4d]" : ""}`}
            >
              <option value="" disabled>
                Seleziona il tuo livello
              </option>
              {stressOptions.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#141414] text-white">
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="blocco-fisiologico"
              className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
            >
              Principale Blocco Fisiologico
            </label>
            <textarea
              id="blocco-fisiologico"
              data-testid="input-blocco-fisiologico"
              required
              rows={4}
              value={form.blocco_fisiologico}
              onChange={update("blocco_fisiologico")}
              placeholder="Descrivi brevemente il tuo principale sintomo di attrito (es. insonnia, brain fog, sbalzi energetici)..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="candidatura-submit-btn"
            className="w-full cursor-pointer border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-[#0D0D0D] transition-colors duration-300 hover:bg-[#F3E5AB] hover:shadow-[0_0_28px_rgba(212,175,55,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Invio in corso…" : "Invia Candidatura Reale"}
          </button>
          <p className="text-center text-[11px] leading-relaxed text-[#4d4d4d]">
            Ogni candidatura viene valutata personalmente. La riservatezza dei
            tuoi dati è garantita.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
